import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  Avatar,
  AvatarSize,
  Button,
  ButtonSize,
  HoverStyles,
  PaddingModes,
  Text,
  ThumbnailImage,
} from 'wix-ui-tpa/cssVars';
import { DiscountName } from '../../DiscountName/DiscountName';
import { ReadMoreLink } from '../../ReadMoreLink/ReadMoreLink';
import { CourseBadge } from '../../CourseBadge/CourseBadge';
import { CourseDetails } from '../../CourseDetails/CourseDetails';
import {
  AgendaItemLayoutProps,
  STAFF_LOCATION_SEPARATOR_DATA_HOOK,
} from '../const';
import { useShowsStaffAndLocation } from '../useShowsStaffAndLocation';
import { classes, st } from './AgendaItemOverlay.st.css';

export const AgendaItemOverlayLayout: React.FC<AgendaItemLayoutProps> = ({
  agendaItemViewModel,
  onSlotClick,
  onReadMoreClick,
}) => {
  const {
    serviceName,
    serviceImage,
    startTime,
    duration,
    staffName,
    staffImage,
    location,
    spots,
    price,
    priceBeforeDiscount,
    priceAfterDiscount,
    discountName,
    showCalculatedAtCheckout,
    buttonText,
    disabled,
    slotId,
    readMoreText,
    readMoreAriaLabel,
    course,
  } = agendaItemViewModel || {};
  const { isMobile } = useEnvironment();
  const showsStaffAndLocation = useShowsStaffAndLocation({
    staffName,
    location,
  });

  return (
    <div
      data-hook="agenda-item-overlay-layout"
      className={st(classes.root, {
        isMobile,
      })}
    >
      <Text className={classes.startTime}>{startTime}</Text>
      <div className={st(classes.content)}>
        <div className={classes.opacityOverlay} />
        <div className={classes.backgroundOverlay} />
        <ThumbnailImage
          fluid
          {...serviceImage}
          className={classes.serviceImage}
        />
        <div className={classes.innerContent}>
          <div className={classes.innerBlockedContent}>
            <div className={classes.serviceDetails}>
              <div className={classes.serviceNameRow}>
                <Text className={classes.serviceName}>{serviceName}</Text>
                <CourseBadge badgeText={course?.badgeText} theme="onImage" />
              </div>
              <ReadMoreLink
                slotId={slotId}
                readMoreText={readMoreText}
                readMoreAriaLabel={readMoreAriaLabel}
                onReadMoreClick={onReadMoreClick}
                theme="onImage"
              />
              {/*
                Outside the wrapper below: it goes to `display: none` when staff
                AND location are both off, which hid the course lines even though
                they have their own settings.
              */}
              <CourseDetails course={course} theme="onImage" />
              <div className={st(classes.staffAndLocationWrapper)}>
                <Avatar
                  className={classes.staffImage}
                  size={AvatarSize.small}
                  src={staffImage}
                  name={staffName}
                />
                <Text className={classes.staffName}>{staffName}</Text>
                {/* Both names, or the dot ends up with nothing on one side. */}
                {showsStaffAndLocation && (
                  <Text
                    className={classes.separatorDot}
                    data-hook={STAFF_LOCATION_SEPARATOR_DATA_HOOK}
                    aria-hidden="true"
                  >
                    •
                  </Text>
                )}
                <Text className={classes.location}>{location}</Text>
              </div>
              <Text className={classes.duration}>{duration}</Text>
              <div className={classes.priceContainer}>
                {priceBeforeDiscount && priceAfterDiscount ? (
                  <div className={classes.pricesRow}>
                    <Text
                      className={classes.priceBeforeDiscount}
                      data-hook="agenda-item-price-before-discount"
                    >
                      {priceBeforeDiscount}
                    </Text>
                    <Text className={classes.price}>{priceAfterDiscount}</Text>
                  </div>
                ) : (
                  <Text className={classes.price}>{price}</Text>
                )}
                <DiscountName
                  discountName={discountName}
                  showCalculatedAtCheckout={showCalculatedAtCheckout}
                />
              </div>
              <Text className={classes.spots}>{spots}</Text>
            </div>
            <div className={classes.actions}>
              {buttonText && (
                <div className={classes.buttonWrapper}>
                  <Button
                    data-hook="agenda-item-cta-button"
                    onClick={() => onSlotClick(slotId)}
                    disabled={disabled}
                    className={classes.button}
                    size={ButtonSize.small}
                    paddingMode={PaddingModes.explicitPaddings}
                    hoverStyle={HoverStyles.underline}
                  >
                    {buttonText}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
